import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InputMask from 'react-input-mask';
import { useUser } from '../../utils/UserContext';
import { PlayVideoIcon } from '../shared/Icons';
import VideoPlayer from '../VideoPlayer';
import { AlertCircle, Smartphone, Loader2, Mail, Lock, ArrowLeft, X } from 'lucide-react';
import InstallPrompt from '../InstallPrompt';
import { getBrand } from '../../data/map';

const SNL_ACCENT = '#22d3ee';

const LoadingTransitionMain = ({ title, message }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
    <div className="bg-gray-900 rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4" style={{ border: `1px solid ${SNL_ACCENT}` }}>
      <div className="flex items-center justify-center mb-4">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: SNL_ACCENT }} />
      </div>
      <h3 className="text-lg font-semibold text-white text-center mb-2">{title}</h3>
      <p className="text-gray-400 text-center">{message}</p>
    </div>
  </div>
);

const CodeLinkLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authStage, setAuthStage] = useState('initial');
  const [error, setError] = useState('');
  const [brand, setBrand] = useState(null);          // set on successful login
  const companylogo = '/static/images/snl_dark_logo.png';
  const { setUser } = useUser();

  // Accent follows the brand once we know who logged in
  const accent = brand?.accent || SNL_ACCENT;
  const logo = brand?.logoDark || brand?.logo || companylogo;
  const appTitle = brand ? `${brand.label} Workflow` : 'SNL Workflow App';

  const gradientTextStyle = brand
    ? {
        color: brand.accent,
        filter: `drop-shadow(0 0 30px ${brand.accent}4d)`,
      }
    : {
        background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 50%, #8b5cf6 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        filter: 'drop-shadow(0 0 30px rgba(34, 211, 238, 0.3))',
      };

  const [showVideo, setShowVideo] = useState(false);
  const ONBOARDING_VIDEO = {
    url: 'https://youtu.be/nfipkklavuI',
    title: 'Onboarding-process',
  };

  const changeStage = (newStage) => {
    setError('');
    setAuthStage(newStage);
  };

  const handleBack = () => {
    setError('');
    setEmail('');
    setMobile('');
    setPassword('');
    changeStage('initial');
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    if (!email || !mobile || !password) {
      setError('Please enter email, mobile number and password');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    const mobileDigits = mobile.replace(/\D/g, '');
    if (mobileDigits.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      // Same path for SNL staff and IGLU South Yarra — the record decides.
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, mobile, password }),
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.user);
        // Prefer the brand the server attached; fall back to the map.js lookup
        setBrand(data.user.brand || getBrand(data.user));
        changeStage('success');

        setTimeout(() => {
          navigate('/profile');
        }, 2000);
      } else {
        setError(data.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleNewUser = () => {
    navigate('/validationpage');
  };

  const inputClass =
    'appearance-none block w-full pl-10 pr-3 py-2 rounded-lg shadow-sm placeholder-gray-400 bg-gray-800/50 text-white border focus:border-blue-500 focus:outline-none';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
      <div className="max-w-lg w-full space-y-8">
        <div className="bg-gray-900/50 rounded-3xl shadow-lg p-8" style={{ border: `1px solid ${accent}` }}>
          <div className="flex items-center justify-center">
            <img src={logo} alt={brand ? `${brand.label} logo` : 'Company Logo'} className="w-24" />
          </div>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold" style={gradientTextStyle}>{appTitle}</h2>
            <p className="mt-2 font-medium flex items-center justify-center gap-2" style={gradientTextStyle}>
              Welcome, sign into your profile..
              <button
                onClick={() => setShowVideo(true)}
                className="inline-flex items-center focus:outline-none"
                style={{ color: accent }}
                title="Watch onboarding video"
                aria-label="Watch onboarding video"
              >
                <PlayVideoIcon />
              </button>
            </p>
          </div>

          {/* Initial Stage - Login/New User Choice */}
          {authStage === 'initial' && (
            <div className="space-y-4">
              <button
                onClick={handleNewUser}
                className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-br from-gray-800 via-gray-700 to-black rounded-full shadow-sm text-lg font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                style={{ border: `1px solid ${accent}` }}
              >
                New User
              </button>

              <button
                onClick={() => changeStage('login')}
                className="w-full flex items-center justify-center px-4 py-3 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black shadow-sm text-lg font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                style={{ border: `1px solid ${accent}` }}
              >
                Login
              </button>
            </div>
          )}

          {authStage === 'login' && (
            <div className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium" style={gradientTextStyle}>
                  Enter your email
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    onKeyPress={handleKeyPress}
                    placeholder="user@example.com"
                    className={inputClass}
                    style={{ borderColor: accent }}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="mobile" className="block text-sm font-medium" style={gradientTextStyle}>
                  Enter your mobile number
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Smartphone className="h-5 w-5 text-gray-400" />
                  </div>
                  <InputMask
                    id="mobile"
                    mask="(999) 999-9999"
                    type="tel"
                    value={mobile}
                    onChange={(e) => { setMobile(e.target.value); setError(''); }}
                    onKeyPress={handleKeyPress}
                    placeholder="(040) 274-2324"
                    className={inputClass}
                    style={{ borderColor: accent }}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium" style={gradientTextStyle}>
                  Enter your password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    onKeyPress={handleKeyPress}
                    placeholder="••••••••"
                    className={inputClass}
                    style={{ borderColor: accent }}
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black shadow-sm text-lg font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ border: `1px solid ${accent}` }}
                >
                  Submit
                </button>

                <button
                  onClick={handleBack}
                  disabled={loading}
                  className="w-full flex items-center justify-center text-lg font-medium disabled:opacity-50"
                  style={gradientTextStyle}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" style={{ color: accent }} />
                  Back Home
                </button>
              </div>
            </div>
          )}

          {/* Success Stage — brand-aware */}
          {authStage === 'success' && (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: accent }} />
              </div>
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                style={{ background: brand ? `${brand.accent}26` : '#bbf7d0' }}
              >
                <svg className="w-8 h-8" style={{ color: brand ? brand.accent : '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium" style={gradientTextStyle}>
                {brand ? `Welcome to ${brand.label}` : 'Successfully logged in!'}
              </h3>
              <p className="text-sm" style={gradientTextStyle}>Redirecting to your profile page...</p>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <LoadingTransitionMain
          title="Processing"
          message="Please wait while we verify your credentials..."
        />
      )}

      {showVideo && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4"
          onClick={() => setShowVideo(false)}
        >
          <div
            className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden w-full md:w-[65vw] max-w-[1200px]"
            style={{ border: `1px solid ${accent}` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3">
              <h3 className="text-lg font-semibold" style={gradientTextStyle}>
                {ONBOARDING_VIDEO.title}
              </h3>
              <button
                onClick={() => setShowVideo(false)}
                className="text-gray-400 hover:text-white focus:outline-none"
                aria-label="Close video"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="w-full" style={{ aspectRatio: '16 / 9', maxHeight: '75vh' }}>
              <VideoPlayer url={ONBOARDING_VIDEO.url} title={ONBOARDING_VIDEO.title} autoPlay muted={false} />
            </div>
          </div>
        </div>
      )}
      <InstallPrompt />
    </div>
  );
};

export default CodeLinkLogin;